import { proxyCustomElement, HTMLElement, h, Host } from '@stencil/core/internal/client';
import { a as checkEmptyOrOneOf, c as checkEmptyOrType } from './index2.js';
import { v as version } from './package.js';
import { c as checkNonEmpty } from './check-non-empty.js';
import { c as checkType } from './check-type.js';

const postIconCss = ":host{display:inline-block;width:1em;height:1em;vertical-align:-0.15em}span{display:block;width:100%;height:100%;fill:currentColor;forced-color-adjust:preserve-parent-color;background-color:currentColor;-webkit-mask-position:center center;mask-position:center center;-webkit-mask-repeat:no-repeat;mask-repeat:no-repeat;-webkit-mask-size:100%;mask-size:100%}:host([flip-h]:not([flip-h=false])){scale:-1 1}:host([flip-v]:not([flip-v=false])){scale:1 -1}:host([flip-h][flip-v]:not([flip-h=false],[flip-v=false])){scale:-1}:host([animation]){transform-origin:center}:host([animation=cylon]){animation:icon-animation-cylon .75s ease-in-out infinite alternate}:host([animation=cylon-vertical]){animation:icon-animation-cylon-vertical .75s ease-in-out infinite alternate}:host([animation=spin]){animation:icon-animation-spin 2s linear infinite normal}:host([animation=spin-reverse]){animation:icon-animation-spin 2s linear infinite reverse}:host([animation=fade]){animation:icon-animation-fade .75s ease-in-out infinite alternate}:host([animation=throb]){animation:icon-animation-throb .75s ease-in-out infinite alternate}@keyframes icon-animation-cylon{from{transform:translateX(-25%)}to{transform:translateX(25%)}}@keyframes icon-animation-cylon-vertical{from{transform:translateY(25%)}to{transform:translateY(-25%)}}@keyframes icon-animation-fade{from{opacity:.1}to{opacity:1}}@keyframes icon-animation-spin{from{transform:rotate(0deg)}to{transform:rotate(359deg)}}@keyframes icon-animation-throb{from{opacity:.5;transform:scale(0.5)}to{opacity:1;transform:scale(1)}}";
const PostIconStyle0 = postIconCss;

const CDN_URL = `https://unpkg.com/@swisspost/design-system-icons@${version}/public/post-icons`;
const ANIMATION_NAMES = [
    'cylon',
    'cylon-vertical',
    'spin',
    'spin-reverse',
    'fade',
    'throb',
];
const ANIMATION_KEYS = [...ANIMATION_NAMES];
const PostIcon = /*@__PURE__*/ proxyCustomElement(class PostIcon extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.animation = null;
        this.base = null;
        this.flipH = false;
        this.flipV = false;
        this.name = undefined;
        this.rotate = null;
        this.scale = null;
    }
    validateAnimation(newValue = this.animation) {
        if (newValue !== undefined)
            checkEmptyOrOneOf(newValue, ANIMATION_KEYS, `The post-icon "animation" prop requires one of the following values: ${ANIMATION_KEYS.join(', ')}.`);
    }
    validateBase(newValue = this.base) {
        checkEmptyOrType(newValue, 'string', 'The post-icon "base" prop should be a string.');
    }
    validateFlipH(newValue = this.flipH) {
        checkEmptyOrType(newValue, 'boolean', 'The post-icon "flipH" prop should be a boolean.');
    }
    validateFlipV(newValue = this.flipV) {
        checkEmptyOrType(newValue, 'boolean', 'The post-icon "flipV" prop should be a boolean.');
    }
    validateName(newValue = this.name) {
        checkNonEmpty(newValue, 'The post-icon "name" prop is required!.');
        checkType(newValue, 'string', 'The post-icon "name" prop should be a string.');
    }
    validateRotate(newValue = this.rotate) {
        checkEmptyOrType(newValue, 'number', 'The post-icon "rotate" prop should be a number.');
    }
    validateScale(newValue = this.scale) {
        checkEmptyOrType(newValue, 'number', 'The post-icon "scale" prop should be a number.');
    }
    // Construct the icon url from different possible sources
    getUrl() {
        var _a, _b, _c, _d, _e, _f, _g;
        // the first definition object which defines a domain, will be used to set the domain of the file url
        // the first definition object which defines a slug, will be used to set the slug of the file url
        const urlDefinitions = [
            getUrlDefinition(this.base, 'both'),
            getUrlDefinition((_a = document.head
                .querySelector('meta[name="design-system-settings"][data-post-icon-base]')) === null || _a === void 0 ? void 0 : _a.getAttribute('data-post-icon-base'), 'relative'),
            getUrlDefinition((_b = document.querySelector('base[href]')) === null || _b === void 0 ? void 0 : _b.getAttribute('href'), 'both'),
        ];
        // in case no other definition defines a domain, the current origin is used as a fallback
        const origin = (_e = (_d = (_c = urlDefinitions.find(d => d.definesDomain)) === null || _c === void 0 ? void 0 : _c.url) === null || _d === void 0 ? void 0 : _d.origin) !== null && _e !== void 0 ? _e : window.location.origin;
        // in case no other definition defines a slug, the cdn url is used as a fallback
        const slug = (_g = (_f = urlDefinitions.find(d => d.definesSlug)) === null || _f === void 0 ? void 0 : _f.url) === null || _g === void 0 ? void 0 : _g.pathname;
        const file = `${this.name}.svg`;
        let url;
        if (slug) {
            url = new URL(`${origin}${slug}/${file}`).toString();
        }
        else {
            url = new URL(`${CDN_URL}/${file}`).toString();
        }
        return url;
        function getUrlDefinition(url, allow) {
            return {
                url: getUrlObject(url),
                definesDomain: allow !== 'relative' ? definesDomain(url) : false,
                definesSlug: allow !== 'absolute' ? definesSlug(url) : false,
            };
            function getUrlObject(url) {
                if (url) {
                    return definesDomain(url) ? new URL(url) : new URL(url, window.location.origin);
                }
                else {
                    return null;
                }
            }
            function definesDomain(url) {
                return url ? /^https?:\/\//.test(url) : false;
            }
            function definesSlug(url) {
                const urlObject = getUrlObject(url);
                return Boolean(/^\/.+/.test(urlObject === null || urlObject === void 0 ? void 0 : urlObject.pathname));
            }
        }
    }
    getStyles() {
        const url = this.getUrl();
        return Object.entries({
            '-webkit-mask-image': `url(${url})`,
            'mask-image': `url('${url}')`,
            'transform': (this.scale && !isNaN(Number(this.scale)) ? 'scale(' + this.scale + ')' : '') +
                (this.rotate && !isNaN(Number(this.rotate)) ? ' rotate(' + this.rotate + 'deg)' : ''),
        })
            .filter(([_key, value]) => value !== null)
            .reduce((styles, [key, value]) => Object.assign(styles, { [key]: value }), {});
    }
    componentDidLoad() {
        this.validateBase();
        this.validateName();
        this.validateFlipH();
        this.validateFlipV();
        this.validateScale();
        this.validateRotate();
        this.validateAnimation();
    }
    render() {
        return (h(Host, { key: '01c59dae4b2d4277f9230534080924359d64542a', "data-version": version }, h("span", { key: '639ccb1dde3fef32e2678abaaf549ac830838158', style: this.getStyles() })));
    }
    get host() { return this; }
    static get watchers() { return {
        "animation": ["validateAnimation"],
        "base": ["validateBase"],
        "flipH": ["validateFlipH"],
        "flipV": ["validateFlipV"],
        "name": ["validateName"],
        "rotate": ["validateRotate"],
        "scale": ["validateScale"]
    }; }
    static get style() { return PostIconStyle0; }
}, [1, "post-icon", {
        "animation": [1],
        "base": [1],
        "flipH": [4, "flip-h"],
        "flipV": [4, "flip-v"],
        "name": [1],
        "rotate": [2],
        "scale": [2]
    }, undefined, {
        "animation": ["validateAnimation"],
        "base": ["validateBase"],
        "flipH": ["validateFlipH"],
        "flipV": ["validateFlipV"],
        "name": ["validateName"],
        "rotate": ["validateRotate"],
        "scale": ["validateScale"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["post-icon"];
    components.forEach(tagName => { switch (tagName) {
        case "post-icon":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, PostIcon);
            }
            break;
    } });
}

export { PostIcon as P, defineCustomElement as d };
